import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  ComponentType,
} from 'react';

import { LDContext } from 'launchdarkly-js-client-sdk';

import { ENVS, getEnv, getRegion } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalContext } from '@adsk/alloy-react-context';
import { noop } from '@adsk/alloy-react-helpers';

import { useOxygenId } from './hooks/useOxygenId';
import createLaunchDarklyContext, {
  DEFAULT_ANONYMOUS_USER_ID,
  LaunchDarklyContextValue,
  LaunchDarklyProviderProps,
} from './LaunchDarklyContext';
import type { EntitlementsUser } from './projectEntitlementTypes';

const LD_API_KEYS = {
  [ENVS.QA]: '5e4c38359d07d807cfb6fc26',
  [ENVS.STAGING]: '5e4c3835aadaaf0815842d0e',
  [ENVS.PRODUCTION]: '5e4c3834d497f707dd287f20',
};

export const AlloyLaunchDarklyContext = getOrCreateGlobalContext<
  LaunchDarklyContextValue | undefined
>(CONTEXT_NAMES.AlloyLaunchDarklyContext);

const {
  SimpleLaunchDarklyProvider,
  useFeatureFlag: useAlloyFeatureFlag,
  useFeatureFlagOnce: useAlloyFeatureFlagOnce,
  useAllFeatureFlags: useAlloyAllFeatureFlags,
} = createLaunchDarklyContext(AlloyLaunchDarklyContext);

type AlloyLaunchDarklyContextValue = {
  projectId?: string | undefined;
  accountId?: string | undefined;
  user?: EntitlementsUser | undefined;
  setUser: (user: EntitlementsUser | undefined) => void;
  setProjectId: (projectId: string | undefined) => void;
  setAccountId: (accountId: string | undefined) => void;
};

const AlloyLaunchDarklyUserContext =
  createContext<AlloyLaunchDarklyContextValue>({
    setUser: noop,
    setProjectId: noop,
    setAccountId: noop,
    user: undefined,
  });

const useAlloyLaunchDarklyUser = (
  user: EntitlementsUser | undefined,
  region: string,
  accountId: string | undefined,
  projectId: string | undefined,
): LDContext => {
  const { analyticsId, email } = user || {};

  const { oxygenId } = useOxygenId();

  return useMemo(() => {
    const custom = { region };

    if (accountId) {
      Object.assign(custom, { accountId });
    }
    if (projectId) {
      Object.assign(custom, { projectId });
    }

    if (oxygenId) {
      Object.assign(custom, { oxygenId });
    }

    return {
      kind: 'user',
      key: analyticsId || DEFAULT_ANONYMOUS_USER_ID,
      anonymous: true,
      email,
      ...custom,
    };
  }, [oxygenId, email, analyticsId, region, accountId, projectId]);
};

const useAlloyLaunchDarklyMetaData = (): AlloyLaunchDarklyContextValue => {
  return useContext(AlloyLaunchDarklyUserContext);
};

type AlloyLaunchDarklyProviderProps = Omit<
  LaunchDarklyProviderProps,
  'apiKey'
> & {
  // Make apiKey optional
  apiKey?: LaunchDarklyProviderProps['apiKey'];
};

const AlloyLaunchDarklyProvider: ComponentType<
  AlloyLaunchDarklyProviderProps
> = (props) => {
  const env = getEnv();
  const apiKey = useMemo(() => LD_API_KEYS[env], [env]);
  const region = getRegion();
  const [user, setUser] = useState<EntitlementsUser | undefined>(undefined);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const ldUser = useAlloyLaunchDarklyUser(user, region, accountId, projectId);
  const options = useMemo(
    () => ({
      allAttributesPrivate: true,
      sendEventsOnlyForVariation: true,
    }),
    [],
  );

  return (
    <AlloyLaunchDarklyUserContext.Provider
      value={{
        projectId,
        accountId,
        user,
        setUser,
        setProjectId,
        setAccountId,
      }}
    >
      <SimpleLaunchDarklyProvider
        user={ldUser}
        apiKey={apiKey}
        options={options}
        {...props}
      />
    </AlloyLaunchDarklyUserContext.Provider>
  );
};

export {
  useAlloyFeatureFlag,
  useAlloyFeatureFlagOnce,
  useAlloyAllFeatureFlags,
  useAlloyLaunchDarklyMetaData,
  AlloyLaunchDarklyProvider,
};
